import { synthesizeSpeech } from './api';

export const micSupported =
  typeof window !== 'undefined' &&
  !!navigator.mediaDevices?.getUserMedia &&
  typeof window.MediaRecorder !== 'undefined';

export const ttsSupported = typeof window !== 'undefined' && typeof Audio !== 'undefined';

// Turn-taking for the voice conversation. One mic stream for the whole
// session feeds both the level meter and the recorder - Android Chrome
// gives the mic to only one capture at a time, which is why the old Web
// Speech API recognizer (plus a second stream for the meter) heard nothing
// there. Speech is detected from the signal's loudness against the room's
// own noise floor; the recording of each utterance goes to the backend's
// Whisper (transcribeSpeech in api.js).
const TICK_MS = 50;
const SPEECH_START_MS = 200; // this long above the threshold = they started talking
const SILENCE_END_MS = 1200; // this long below it after speaking = their turn is over
const MIN_UTTERANCE_MS = 400;
const MAX_UTTERANCE_MS = 30000;
// While waiting for them to start, the recording restarts this often so a
// long quiet wait isn't uploaded along with what they finally say.
const MAX_IDLE_MS = 15000;

function pickRecorderType() {
  const types = ['audio/webm;codecs=opus', 'audio/webm', 'audio/mp4', 'audio/ogg;codecs=opus'];
  return types.find((type) => window.MediaRecorder.isTypeSupported?.(type)) || '';
}

/**
 * Starts listening. Callbacks: onLevel(0..1) for the orb, onState('listening'
 * | 'paused'), onSpeechStart() (used for barge-in), onUtterance(blob).
 * isAiSpeaking() raises the bar for what counts as speech while the reply is
 * playing, so the speakers' own echo doesn't interrupt it. Throws if the mic
 * can't be opened (permission denied, no device).
 */
export async function openVoiceListener({ onLevel, onState, onSpeechStart, onUtterance, isAiSpeaking }) {
  const stream = await navigator.mediaDevices.getUserMedia({
    audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }
  });
  const AudioContextImpl = window.AudioContext || window.webkitAudioContext;
  const audioContext = new AudioContextImpl();
  await audioContext.resume().catch(() => {});
  const analyser = audioContext.createAnalyser();
  analyser.fftSize = 1024;
  audioContext.createMediaStreamSource(stream).connect(analyser);
  const samples = new Float32Array(analyser.fftSize);
  const mimeType = pickRecorderType();

  let recorder = null;
  let chunks = [];
  let paused = true;
  let closed = false;
  let noiseFloor = 0.01;
  let loudMs = 0;
  let quietMs = 0;
  let speaking = false;
  let speechMs = 0;
  let idleMs = 0;

  const startRecorder = () => {
    chunks = [];
    recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunks.push(event.data);
    };
    recorder.start();
    speaking = false;
    loudMs = 0;
    quietMs = 0;
    speechMs = 0;
    idleMs = 0;
  };

  // Stops the current recording; resolves to its audio, or null when it's
  // being discarded.
  const stopRecorder = (keep) => new Promise((resolve) => {
    const current = recorder;
    recorder = null;
    if (!current || current.state === 'inactive') {
      resolve(null);
      return;
    }
    current.onstop = () => resolve(keep ? new Blob(chunks, { type: current.mimeType || mimeType }) : null);
    current.stop();
  });

  const finishUtterance = async () => {
    paused = true;
    onState?.('paused');
    const blob = await stopRecorder(true);
    if (blob && !closed) onUtterance?.(blob);
  };

  const tick = () => {
    analyser.getFloatTimeDomainData(samples);
    let sum = 0;
    for (let i = 0; i < samples.length; i++) sum += samples[i] * samples[i];
    const rms = Math.sqrt(sum / samples.length);
    onLevel?.(paused ? 0 : Math.min(rms * 12, 1));
    if (paused || !recorder) return;

    const threshold = isAiSpeaking?.() ? Math.max(0.05, noiseFloor * 6) : Math.max(0.015, noiseFloor * 3);
    const loud = rms > threshold;
    if (!speaking && !loud) {
      // Slowly follows the room's background noise while nobody talks.
      noiseFloor = noiseFloor * 0.95 + rms * 0.05;
    }

    if (!speaking) {
      loudMs = loud ? loudMs + TICK_MS : 0;
      idleMs += TICK_MS;
      if (loudMs >= SPEECH_START_MS) {
        speaking = true;
        speechMs = loudMs;
        quietMs = 0;
        onSpeechStart?.();
      } else if (idleMs >= MAX_IDLE_MS) {
        stopRecorder(false).then(() => {
          if (!paused && !closed) startRecorder();
        });
      }
      return;
    }

    speechMs += TICK_MS;
    quietMs = loud ? 0 : quietMs + TICK_MS;
    if (speechMs >= MAX_UTTERANCE_MS || quietMs >= SILENCE_END_MS) {
      if (speechMs - quietMs < MIN_UTTERANCE_MS) {
        // A cough or a bump, not a turn - keep listening.
        stopRecorder(false).then(() => {
          if (!paused && !closed) startRecorder();
        });
      } else {
        finishUtterance();
      }
    }
  };
  const timer = setInterval(tick, TICK_MS);

  return {
    resume() {
      if (closed || !paused) return;
      paused = false;
      startRecorder();
      onState?.('listening');
    },
    pause() {
      if (closed || paused) return;
      paused = true;
      stopRecorder(false);
      onState?.('paused');
    },
    close() {
      if (closed) return;
      closed = true;
      paused = true;
      clearInterval(timer);
      stopRecorder(false);
      stream.getTracks().forEach((track) => track.stop());
      audioContext.close().catch(() => {});
      onLevel?.(0);
      onState?.('paused');
    }
  };
}

// Curated, single-speaker, high-quality Piper voices baked into the
// backend - kept in sync by hand with CURATED_VOICES in
// backend/app/services/tts.py.
export const CURATED_VOICES = {
  es: [
    { voiceURI: 'es_AR-daniela-high', name: 'Daniela (Argentina, femenina)' },
    { voiceURI: 'es_MX-claude-high', name: 'Claude (México, masculina)' },
    { voiceURI: 'es_ES-davefx-medium', name: 'Davefx (España, masculina)' },
    { voiceURI: 'es_MX-ald-medium', name: 'Ald (México, masculina)' },
    { voiceURI: 'es_ES-sharvard-medium', name: 'Sharvard (España, masculina)' }
  ],
  en: [
    // Lessac was mislabeled "male" - it's a female voice (Piper's highest
    // quality US English corpus).
    { voiceURI: 'en_US-lessac-high', name: 'Lessac (US, female)' },
    { voiceURI: 'en_US-ryan-high', name: 'Ryan (US, male)' },
    { voiceURI: 'en_GB-cori-high', name: 'Cori (UK, female)' },
    { voiceURI: 'en_GB-alan-medium', name: 'Alan (UK, male)' }
  ]
};

export function defaultVoiceFor(language) {
  return (CURATED_VOICES[language] || CURATED_VOICES.en)[0].voiceURI;
}

// The app's name is a play on "when I see us" and should always be spoken
// that way (in English) regardless of which voice/language is speaking -
// left as-is, Piper would try to sound it out as one made-up word.
function spellOutAppName(text) {
  return text.replace(/wheniseeus/gi, 'When I see us');
}

function stripMarkdownForSpeech(text) {
  return spellOutAppName(text)
    .replace(/```[\s\S]*?```/g, '')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/!\[[^\]]*]\([^)]*\)/g, '')
    .replace(/\[([^\]]+)]\([^)]*\)/g, '$1')
    .replace(/[#*_>~]/g, '');
}

let currentAudio = null;
let currentAudioContext = null;
let currentLevelFrame = null;

function stopLevelMeter() {
  if (currentLevelFrame) {
    cancelAnimationFrame(currentLevelFrame);
    currentLevelFrame = null;
  }
  if (currentAudioContext) {
    currentAudioContext.close().catch(() => {});
    currentAudioContext = null;
  }
}

export function stopSpeaking() {
  stopLevelMeter();
  if (currentAudio) {
    currentAudio.pause();
    currentAudio = null;
  }
}

// Analyses the actual TTS playback in real time (same Web Audio approach
// App.js uses for the mic while listening) so the voice-mode orb's gradient
// can pulse to the rhythm of the voice actually being spoken, instead of a
// canned animation unrelated to what's playing.
function startLevelMeter(audio, onLevel) {
  try {
    const AudioContextImpl = window.AudioContext || window.webkitAudioContext;
    const audioContext = new AudioContextImpl();
    currentAudioContext = audioContext;
    // Routing the element through the analyser and on to the destination
    // is required for getByteFrequencyData to see anything - it doesn't
    // change what's audible.
    const source = audioContext.createMediaElementSource(audio);
    const analyser = audioContext.createAnalyser();
    analyser.fftSize = 256;
    source.connect(analyser);
    analyser.connect(audioContext.destination);

    const data = new Uint8Array(analyser.frequencyBinCount);
    const tick = () => {
      if (currentAudio !== audio) return;
      analyser.getByteFrequencyData(data);
      const avg = data.reduce((sum, value) => sum + value, 0) / data.length;
      onLevel(Math.min(avg / 90, 1));
      currentLevelFrame = requestAnimationFrame(tick);
    };
    tick();
  } catch (error) {
    console.error(error);
  }
}

// Synthesizes via the backend's local neural TTS (Piper) and plays the
// result - replaces the browser's built-in (often robotic) voices.
export async function speak(text, language, voiceURI, { onEnd, onLevel } = {}) {
  if (!ttsSupported || !text) {
    onEnd?.();
    return;
  }
  stopSpeaking();

  try {
    const blob = await synthesizeSpeech({
      mensaje: stripMarkdownForSpeech(text),
      voz: voiceURI || defaultVoiceFor(language)
    });
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    currentAudio = audio;

    if (onLevel) startLevelMeter(audio, onLevel);

    const finish = () => {
      URL.revokeObjectURL(url);
      stopLevelMeter();
      if (currentAudio === audio) currentAudio = null;
      onLevel?.(0);
      onEnd?.();
    };
    audio.onended = finish;
    audio.onerror = finish;

    await audio.play();
  } catch (error) {
    console.error(error);
    onEnd?.();
  }
}
